// Token refresh service for maintaining user sessions
import axios from 'axios';

// Token refresh configuration
const REFRESH_BUFFER_TIME = 60 * 1000; // 1 minute buffer before expiry
let refreshTimeout: ReturnType<typeof setTimeout> | null = null;
let isInitialized = false;

/**
 * Set up token refresh mechanism
 */
export const setupTokenRefresh = async () => {
  // Clear any existing timeout
  if (refreshTimeout) {
    clearTimeout(refreshTimeout);
    refreshTimeout = null;
  }

  // Check if user is authenticated
  if (!window.currentUser) {
    return; // Not authenticated, nothing to do
  }

  try {
    // Use the singleton validation function to prevent duplicate requests
    const response = await validateTokenSingleton();

    if (response.status === 200) {
      // Schedule regular token refresh - refresh every 10 minutes to stay well ahead of expiry
      const refreshInterval = 10 * 60 * 1000; // 10 minutes
      refreshTimeout = setTimeout(refreshToken, refreshInterval);
    }
  } catch (error) {
    // If validation fails, attempt an immediate token refresh
    refreshToken();
  }
};

/**
 * Refresh the authentication tokens
 */
// Track the last refresh time to prevent refresh loops
let lastRefreshTime = 0;
const REFRESH_COOLDOWN = 5000; // 5 seconds minimum between refresh attempts

export const refreshToken = async () => {
  // Prevent refresh loops by enforcing a cooldown period
  const now = Date.now();
  if (now - lastRefreshTime < REFRESH_COOLDOWN) {
    return false;
  }

  lastRefreshTime = now;

  try {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
    const response = await axios.post('/api/v1/refresh_token', {}, {
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken || ''
      },
      withCredentials: true
    });

    if (response.status === 200) {
      // Clear any cached validation responses since tokens have changed
      validateTokenCache = null;
      validateTokenCacheTime = 0;
      pendingValidationRequest = null;

      // Update window.currentUser with user info if provided
      if (response.data?.user) {
        window.currentUser = response.data.user;
      }

      // Schedule the next token refresh - use consistent 10 minute interval
      const refreshInterval = 10 * 60 * 1000; // 10 minutes
      refreshTimeout = setTimeout(refreshToken, refreshInterval);
      return true;
    }
    return false;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      // Clear cache since tokens are invalid
      validateTokenCache = null;
      validateTokenCacheTime = 0;
      pendingValidationRequest = null;

      // Only logout if we're currently authenticated (avoid logout loops during initialization)
      if (window.currentUser) {
        window.currentUser = null;
        // Redirect to sign in
        window.location.href = '/users/sign_in';
      }
    }

    return false;
  }
};

// Global caching for token validation requests
let validateTokenCache: any = null;
let validateTokenCacheTime = 0;
const CACHE_DURATION = 3000; // 3 seconds

// Keep track of in-flight validation request to deduplicate simultaneous calls
let pendingValidationRequest: Promise<any> | null = null;

/**
 * Creates a singleton validation request that all components can share
 */
export const validateTokenSingleton = async () => {
  const now = Date.now();

  // Return cached response if available and recent
  if (validateTokenCache && now - validateTokenCacheTime < CACHE_DURATION) {
    return validateTokenCache;
  }

  // If there's already a request in flight, return that promise
  if (pendingValidationRequest) {
    return pendingValidationRequest;
  }

  // Create a new request
  const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
  pendingValidationRequest = axios.get('/api/v1/validate_token', {
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrfToken || ''
    },
    withCredentials: true
  })
    .then(response => {
      validateTokenCache = response;
      validateTokenCacheTime = Date.now();
      return response;
    })
    .catch(error => {
      throw error;
    })
    .finally(() => {
      // Clear the pending request reference after completion
      setTimeout(() => {
        pendingValidationRequest = null;
      }, 100);
    });

  return pendingValidationRequest;
};

// Export a function to initialize token refresh when the app starts
export const initializeTokenRefresh = () => {
  // Prevent duplicate initialization
  if (isInitialized) {
    return;
  }

  isInitialized = true;

  // Add axios response interceptor for handling 401 errors
  axios.interceptors.response.use(
    (response) => {
      // Cache successful validation responses
      if (response.config.url === '/api/v1/validate_token' && response.config.method === 'get') {
        validateTokenCache = response;
        validateTokenCacheTime = Date.now();
      }
      return response;
    },
    async (error) => {
      const originalRequest = error.config;

      // If the error is unauthorized and we haven't tried refreshing yet
      if (error.response && error.response.status === 401) {
        // Don't retry for validate_token endpoint to avoid loops
        if (error.config.url !== '/api/v1/validate_token' && !originalRequest._retry) {
          originalRequest._retry = true;

          // Try to refresh the token
          const refreshSuccessful = await refreshToken();
          if (refreshSuccessful) {
            // Retry the original request
            return axios(originalRequest);
          }
        }
      }

      // If refresh didn't work or this isn't a 401 error, pass it through
      return Promise.reject(error);
    }
  );

  // Set up initial token refresh scheduling
  setupTokenRefresh();
};
